import React, { useEffect, useState, useRef } from "react";
import { X, Play, ArrowCounterClockwise, CheckCircle } from "@phosphor-icons/react";
import api from "../../lib/api";

// Palabras + colores (key, hex, label).
const COLORS = [
  { key: "rojo", hex: "#FF3B30", label: "ROJO" },
  { key: "verde", hex: "#32D74B", label: "VERDE" },
  { key: "amarillo", hex: "#FFD60A", label: "AMARILLO" },
  { key: "azul", hex: "#007AFF", label: "AZUL" },
];
const TOTAL_TRIALS = 20;

/**
 * Test de Stroop: muestra una palabra escrita ("ROJO") pintada en un color
 * que NO necesariamente coincide ("verde"). El atleta debe tocar el botón
 * del COLOR REAL en que está escrita (ignorar el significado). Mide RT
 * y aciertos.
 */
export default function StroopPlayer({ onClose }) {
  const [phase, setPhase] = useState("intro");
  const [trial, setTrial] = useState(0);
  const [current, setCurrent] = useState(null); // {word, color}
  const [correct, setCorrect] = useState(0);
  const [rts, setRts] = useState([]);
  const shownAtRef = useRef(null);
  const startedAtRef = useRef(null);

  const newTrial = () => {
    const wordObj = COLORS[Math.floor(Math.random() * COLORS.length)];
    const colorObj = COLORS[Math.floor(Math.random() * COLORS.length)];
    setCurrent({ word: wordObj.label, color: colorObj });
    shownAtRef.current = performance.now();
  };

  const start = () => {
    startedAtRef.current = new Date();
    setTrial(0);
    setCorrect(0);
    setRts([]);
    setPhase("playing");
    newTrial();
  };

  const tap = (colorKey) => {
    if (phase !== "playing" || !current) return;
    const rt = performance.now() - shownAtRef.current;
    const isCorrect = colorKey === current.color.key;
    setRts((arr) => [...arr, rt]);
    if (isCorrect) setCorrect((c) => c + 1);
    const next = trial + 1;
    if (next >= TOTAL_TRIALS) {
      setPhase("done");
    } else {
      setTrial(next);
      newTrial();
    }
  };

  useEffect(() => {
    if (phase !== "done" || !startedAtRef.current) return;
    const avg = rts.length ? rts.reduce((a, b) => a + b, 0) / rts.length : null;
    api.post("/neuroscience/results", {
      config_id: "default_stroop",
      started_at: startedAtRef.current.toISOString(),
      finished_at: new Date().toISOString(),
      total_taps: TOTAL_TRIALS,
      correct_taps: correct,
      avg_reaction_ms: avg,
      notes: `Stroop · ${correct}/${TOTAL_TRIALS} aciertos`,
    }).catch(() => {});
  }, [phase, correct, rts]);

  const avgMs = rts.length ? Math.round(rts.reduce((a, b) => a + b, 0) / rts.length) : null;

  return (
    <div className="fixed inset-0 z-[70] bg-app flex flex-col items-center justify-center p-6" data-testid="stroop-player">
      <button onClick={onClose} className="absolute top-3 right-3 w-10 h-10 flex items-center justify-center bg-black/60 text-white" data-testid="stroop-close">
        <X size={18} weight="bold" />
      </button>

      {phase === "intro" && (
        <div className="text-center max-w-md text-white">
          <div className="font-display font-black uppercase text-3xl tracking-tighter mb-3">Stroop</div>
          <p className="text-muted text-sm mb-6 leading-relaxed">
            Vas a ver una palabra de color escrita en un color que puede no coincidir. Tocá el botón del <strong className="text-white">COLOR en que está escrita</strong>, no de lo que dice. {TOTAL_TRIALS} rondas.
          </p>
          <button onClick={start} className="btn-primary w-full !py-4 flex items-center justify-center gap-2" data-testid="stroop-start">
            <Play size={20} weight="fill" /> Empezar
          </button>
        </div>
      )}

      {phase === "playing" && current && (
        <div className="w-full max-w-md text-center">
          <div className="absolute top-4 left-1/2 -translate-x-1/2 text-white/70 text-xs uppercase tracking-widest">
            {trial + 1}/{TOTAL_TRIALS} · Aciertos: <span className="text-[#32D74B] font-bold">{correct}</span>
          </div>
          <div className="font-display font-black text-6xl sm:text-7xl tracking-tighter mb-10 select-none" style={{ color: current.color.hex }} data-testid="stroop-word">
            {current.word}
          </div>
          <div className="grid grid-cols-2 gap-3" data-testid="stroop-buttons">
            {COLORS.map((c) => (
              <button
                key={c.key}
                onClick={() => tap(c.key)}
                className="py-5 font-display font-black uppercase tracking-tighter text-base border-2 border-app hover:border-white"
                style={{ backgroundColor: c.hex, color: c.key === "amarillo" ? "#000" : "#fff" }}
                data-testid={`stroop-btn-${c.key}`}
              >
                {c.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {phase === "done" && (
        <div className="text-center max-w-md text-white">
          <CheckCircle size={48} weight="duotone" className="text-[#32D74B] mx-auto mb-4" />
          <div className="font-display font-black uppercase text-3xl tracking-tighter mb-4">¡Listo!</div>
          <div className="bg-[#141414] border border-app p-4 mb-6 grid grid-cols-2 gap-3 text-center">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted">Aciertos</div>
              <div className="font-display font-black text-2xl text-[#32D74B] mt-1">{correct}/{TOTAL_TRIALS}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted">RT prom</div>
              <div className="font-display font-black text-2xl text-primary-app mt-1">{avgMs ? `${avgMs}ms` : "—"}</div>
            </div>
          </div>
          <div className="flex gap-3">
            <button onClick={() => setPhase("intro")} className="btn-secondary flex-1 flex items-center justify-center gap-2" data-testid="stroop-replay">
              <ArrowCounterClockwise size={16} weight="bold" /> Otra vez
            </button>
            <button onClick={onClose} className="btn-primary flex-1" data-testid="stroop-finish">Terminar</button>
          </div>
        </div>
      )}
    </div>
  );
}
